import type {
  Block,
  ChatMessage,
  GoalTaskBlock,
} from '@/entities/chat-message';
import { AddedTaskLine } from './AddedTaskLine';
import { RevisedTaskLine } from './RevisedTaskLine';
import { SkippedTaskLine } from './SkippedTaskLine';
import { TaskCard } from './TaskCard';

interface ActionCallbacks {
  onAddToPlan?: (messageId: string, blockIndex: number) => void;
  onSkip?: (messageId: string, blockIndex: number) => void;
  disabled?: boolean;
}

interface MessageProps extends ActionCallbacks {
  message: ChatMessage;
}

interface BlockRendererProps extends ActionCallbacks {
  block: Block;
  messageId: string;
  originalIndex: number;
}

function BlockRenderer({
  block,
  messageId,
  originalIndex,
  onAddToPlan,
  onSkip,
  disabled,
}: BlockRendererProps) {
  if (block.type === 'entity') {
    if (!block.data.parentGoalId) {
      return null;
    }
    const goalTask = block as unknown as GoalTaskBlock;
    if (goalTask.status === 'added') {
      return <AddedTaskLine task={goalTask} />;
    }
    if (goalTask.status === 'skipped') {
      return <SkippedTaskLine task={goalTask} />;
    }
    if (goalTask.status === 'revised') {
      return <RevisedTaskLine task={goalTask} />;
    }
    return (
      <TaskCard
        task={goalTask}
        messageId={messageId}
        blockIndex={originalIndex}
        onAddToPlan={onAddToPlan}
        onSkip={onSkip}
        disabled={disabled}
      />
    );
  }

  return (
    <span className="text-sm whitespace-pre-wrap text-gray-700">
      {block.data}
    </span>
  );
}

function UserMessage({ message }: { message: ChatMessage }) {
  return (
    <div className="flex items-start justify-end gap-3">
      <div className="flex max-w-[85%] flex-col gap-3 rounded-[16px] rounded-br-[4px] bg-slate-100 px-3.5 py-2">
        {message.blocks.map((block, i) => (
          <span key={i} className="text-sm whitespace-pre-wrap text-gray-900">
            {block.type === 'text' ? block.data : null}
          </span>
        ))}
      </div>
    </div>
  );
}

interface VisibleBlock {
  block: Block;
  originalIndex: number;
}

function getVisibleBlocks(blocks: Block[]): VisibleBlock[] {
  const firstResolvedIndex = blocks.findIndex(
    (b) => b.type === 'entity' && b.status !== 'suggested',
  );

  if (firstResolvedIndex === -1) {
    return blocks.map((block, i) => ({ block, originalIndex: i }));
  }

  return blocks
    .map((block, i) => ({ block, originalIndex: i }))
    .filter(
      ({ block, originalIndex }) =>
        originalIndex <= firstResolvedIndex || block.type === 'entity',
    );
}

function AiMessage({
  message,
  onAddToPlan,
  onSkip,
  disabled,
}: {
  message: ChatMessage;
} & ActionCallbacks) {
  const visibleBlocks = getVisibleBlocks(message.blocks);

  return (
    <div className="flex items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {visibleBlocks.map(({ block, originalIndex }) => (
          <BlockRenderer
            key={`${message.id}-${originalIndex.toString()}`}
            block={block}
            messageId={message.id}
            originalIndex={originalIndex}
            onAddToPlan={onAddToPlan}
            onSkip={onSkip}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

export function Message({
  message,
  onAddToPlan,
  onSkip,
  disabled,
}: MessageProps) {
  if (message.role === 'user') {
    return <UserMessage message={message} />;
  }

  return (
    <AiMessage
      message={message}
      onAddToPlan={onAddToPlan}
      onSkip={onSkip}
      disabled={disabled}
    />
  );
}
